import { useMemo } from 'react';
import { useGetTenantInfoQuery } from '../services/tenantApi';

// Constants for theme system
const THEME_CACHE_KEY = 'kuppion_theme_cache_v3';
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes
const MAX_CACHE_AGE = 24 * 60 * 60 * 1000; // 24 hours

// Default theme values
const DEFAULT_THEME = {
  primaryColor: '#3A9355',
  secondaryColor: '#152136',
  visualIdentity: {
    name: 'Company',
    logo: null
  }
};

// Utility functions
const isValidHexColor = (color) => {
  if (!color || typeof color !== 'string') return false;
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
};

const sanitizeColor = (color) => {
  if (!color || typeof color !== 'string') return null;
  if (isValidHexColor(color)) return color;
  return null;
};

const getCachedTheme = () => {
  try {
    const cached = localStorage.getItem(THEME_CACHE_KEY);
    if (!cached) return null;
    
    const parsed = JSON.parse(cached);
    const age = Date.now() - (parsed.timestamp || 0);
    
    // Cache is too old
    if (age > MAX_CACHE_AGE) {
      localStorage.removeItem(THEME_CACHE_KEY);
      return null;
    }
    
    // Cache is stale but usable
    const isStale = age > CACHE_DURATION;
    
    return {
      ...parsed.data,
      isCached: true,
      isStale,
      lastUpdated: parsed.timestamp
    };
  } catch (error) {
    console.warn('Failed to get cached theme:', error);
    localStorage.removeItem(THEME_CACHE_KEY);
    return null;
  }
};

const setCachedTheme = (data) => {
  try {
    if (!data) return;
    
    const sanitizedData = {
      primaryColor: sanitizeColor(data.primaryColor),
      secondaryColor: sanitizeColor(data.secondaryColor),
      visualIdentity: {
        name: data.visualIdentity?.name || DEFAULT_THEME.visualIdentity.name,
        logo: data.visualIdentity?.logo || null
      }
    };

    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify({
      data: sanitizedData,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.warn('Failed to cache theme:', error);
  }
};

// Clear all theme-related cache when tenant changes
const clearAllThemeCache = () => {
  const THEME_CACHE_KEYS = [
    'kuppion_theme_cache_v3',
    'kuppion_theme_v2', 
    'kuppion_theme',
    'THEME_STORAGE_KEY'
  ];
  
  THEME_CACHE_KEYS.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove theme cache ${key}:`, error);
    }
  });
};

const useTheme = (tenantId = null, options = {}) => {
  const { skipApiCall = false } = options;

  const shouldSkip = skipApiCall || !tenantId;

  const {
    data: apiData,
    error: apiError,
    isLoading: apiLoading,
    refetch
  } = useGetTenantInfoQuery(tenantId, {
    skip: shouldSkip,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true
  });

  const themeState = useMemo(() => {
    const cached = getCachedTheme();

    if (apiData?.value) {
      const value = apiData.value;
      const processed = {
        primaryColor: sanitizeColor(value.primaryColor) || DEFAULT_THEME.primaryColor,
        secondaryColor: sanitizeColor(value.secondaryColor) || DEFAULT_THEME.secondaryColor,
        visualIdentity: {
          name: value.name || DEFAULT_THEME.visualIdentity.name,
          logo: value.logo || null
        },
        isCached: false,
        isLoading: false,
        isValid: true
      };
      setCachedTheme(processed);
      return processed;
    }

    if (apiLoading && cached && !cached.isStale) {
      return { ...cached, isLoading: true, isCached: true, isValid: true };
    }

    return { ...DEFAULT_THEME, isLoading: apiLoading, isCached: false, isValid: true };
  }, [apiData, apiLoading]);

  const withOpacity = useMemo(() => {
    return (color, opacity) => {
      if (!color || typeof color !== 'string') return `rgba(58, 147, 85, ${opacity})`;
      if (!color.startsWith('#')) return `rgba(58, 147, 85, ${opacity})`;
      try {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      } catch (error) {
        return `rgba(58, 147, 85, ${opacity})`;
      }
    };
  }, []);

  const cssVariables = useMemo(() => {
    return {
      '--primary-color': themeState.primaryColor,
      '--secondary-color': themeState.secondaryColor,
      '--primary-50': withOpacity(themeState.primaryColor, 0.05),
      '--primary-100': withOpacity(themeState.primaryColor, 0.1),
      '--primary-200': withOpacity(themeState.primaryColor, 0.2),
      '--primary-500': themeState.primaryColor,
      '--primary-600': withOpacity(themeState.primaryColor, 0.8),
      '--primary-900': withOpacity(themeState.primaryColor, 0.9),
      '--secondary-50': withOpacity(themeState.secondaryColor, 0.05),
      '--secondary-100': withOpacity(themeState.secondaryColor, 0.1),
      '--secondary-200': withOpacity(themeState.secondaryColor, 0.2),
      '--secondary-500': themeState.secondaryColor,
      '--secondary-600': withOpacity(themeState.secondaryColor, 0.8),
      '--secondary-900': withOpacity(themeState.secondaryColor, 0.9)
    };
  }, [themeState.primaryColor, themeState.secondaryColor, withOpacity]);

  const palette = useMemo(() => ({
    primary50: withOpacity(themeState.primaryColor, 0.05),
    primary100: withOpacity(themeState.primaryColor, 0.1),
    primary200: withOpacity(themeState.primaryColor, 0.2),
    primary500: themeState.primaryColor,
    primary600: withOpacity(themeState.primaryColor, 0.8),
    primary900: withOpacity(themeState.primaryColor, 0.9),
    secondary50: withOpacity(themeState.secondaryColor, 0.05),
    secondary100: withOpacity(themeState.secondaryColor, 0.1),
    secondary200: withOpacity(themeState.secondaryColor, 0.2),
    secondary500: themeState.secondaryColor,
    secondary600: withOpacity(themeState.secondaryColor, 0.8),
    secondary900: withOpacity(themeState.secondaryColor, 0.9)
  }), [themeState.primaryColor, themeState.secondaryColor, withOpacity]);

  return useMemo(() => ({
    ...themeState,
    cssVariables,
    palette,
    withOpacity,
    refetch
  }), [themeState, cssVariables, palette, withOpacity, refetch]);
};

export default useTheme;